import React, { useCallback, useState } from 'react'
import { NotificationInformationCard } from './NotificationInformationCard'
import { Box, Typography } from '@mui/material'
import SpaceBetweenSection from '../../Elements/SpaceBetweenSection'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import DynamicBreadcrumbs from '../../Elements/DynamicBreadcrumbs'
import ActionButtons, { ActionButtonsProps } from '../../Buttons/ActionButtons'
import { ContainedButtonVariant } from '../../Buttons/ContainedButton'
import { TextButtonVariant } from '../../Buttons/TextButton'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { useNotificationContext } from '../../Context/NotificationContext'
import {
  SnackbarSeverity,
  useSnackbarContext,
} from '../../Context/SnackbarContext'
import { OutlinedButtonVariant } from '../../Buttons/OutlinedButton'
import { NotificationRegionsCard } from './NotificationRegionsCard'
import { useShowOnDesktop } from '../../../hooks/useShowOnDesktop'
import { NotificationRolesCard } from './NotificationRolesCard'
import TitleContext from '../../../TitleContext'

export interface NotificationFormCardProps {
  isEditVariant?: boolean
  /** Submission of the Notifications for per add or edit */
  handleFormSubmit: () => Promise<void>
  /** Optional cancel handler, to set the details back on edit for instance */
  handleCancel?: () => void
}

/**
 * The parent component utilizing edits and adds for notifications
 * while displaying the same children components to render applicable
 * fields for an Admin Notification.
 */
export const NotificationFormCard: React.FC<NotificationFormCardProps> = ({
  handleFormSubmit,
  isEditVariant,
  handleCancel,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { useTitleEffect } = React.useContext(TitleContext)
  useTitleEffect(t('Notifications.NotificationFormCard.Title', 'Notifications'))
  const {
    Notification: { createNotification, editNotification },
  } = useLoadingIds()
  const loadingId = isEditVariant ? editNotification : createNotification
  const {
    formValidity,
    resetEditContextValues,
    notificationDetails,
    updateFormValidity,
  } = useNotificationContext()

  const { setSnackbarMessage, setSnackbarSeverity, setSnackbarState } =
    useSnackbarContext()
  const isShowOnDesktop = useShowOnDesktop()

  /** We only start off in edit when adding a notification */
  const [isEditMode, setIsEditMode] = useState(!isEditVariant)

  /** Notifications breadcrumb click */
  const handleRedirect = useCallback(() => {
    resetEditContextValues()
    navigate('/admin/notifications')
  }, [navigate, resetEditContextValues])

  // TODO: Implement region key checks
  const fieldsDoValidate = useCallback(() => {
    updateFormValidity({
      ...formValidity,
      name: { input: notificationDetails.name.length > 0 },
      body: { input: notificationDetails.body.length > 0 },
      regions: { input: notificationDetails.regionKeys.length > 0 },
      roles: { input: notificationDetails.roleKeys.length > 0 },
    })

    return (
      notificationDetails.name.length > 0 &&
      notificationDetails.body.length > 0 &&
      notificationDetails.regionKeys.length > 0 &&
      notificationDetails.roleKeys.length > 0 &&
      notificationDetails.endDate > notificationDetails.startDate &&
      formValidity.endDate.input &&
      formValidity.endTime.input &&
      formValidity.startDate.input &&
      formValidity.startTime.input
    )
  }, [formValidity, updateFormValidity, notificationDetails])

  /**
   * Triggers the handleFormSubmit provided by either
   * the edit or the add notification page. This is
   * different because add will call createNotification
   * and edit will call editNotification. This is separate
   * from the submit below so we can consolidate form
   * validation.
   */
  const { triggerFetch } = useLoadingContext({
    asyncFunction: handleFormSubmit,
    loadingId,
  })

  const validateAndSubmit = useCallback(async () => {
    if (fieldsDoValidate()) {
      // If we return, we don't set SnackbarMessages after and no need for an else
      setIsEditMode(false)
      return triggerFetch()
    }
    setSnackbarMessage(
      t(
        'Notifications.NotificationFormCard.Error.RequiredFields',
        'Please resolve all errors.'
      )
    )
    setSnackbarSeverity(SnackbarSeverity.Error)
    setSnackbarState(true)
  }, [
    fieldsDoValidate,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
    t,
    triggerFetch,
  ])

  /**
   * Submit the form for validation, including all
   * card in NotificationFormCard.
   */
  const submit = () => {
    return setIsEditMode(true)
  }
  /**
   * This is our own loadingId which does not
   * represent an endpoint call, which is why
   * it's not completely relevant to useLoadingIds.
   *
   */
  const primaryButtonLoadingId = isEditVariant ? 'toggleEdit' : 'submit'
  useLoadingContext({
    asyncFunction: validateAndSubmit,
    loadingId: primaryButtonLoadingId,
  })

  const getActionButtonProps = useCallback((): ActionButtonsProps => {
    let secondaryClick: undefined | (() => void) = handleRedirect
    let secondaryButtonLabel:
      | TextButtonVariant
      | OutlinedButtonVariant
      | undefined = TextButtonVariant.Cancel
    let primaryButtonLabel: ContainedButtonVariant = ContainedButtonVariant.Save

    if (isEditVariant) {
      secondaryClick = () => {
        handleCancel?.()
        setIsEditMode(false)
      }
      if (!isEditMode) {
        primaryButtonLabel = ContainedButtonVariant.Edit
        secondaryButtonLabel = TextButtonVariant.Back
        secondaryClick = handleRedirect
      }
    }

    return {
      primaryButtonLabel,
      primaryButtonLoadingId,
      primaryClick: !isEditMode && isEditVariant ? submit : undefined,
      useBaseButton: !isEditVariant || (isEditMode && isEditVariant),
      secondaryButtonLabel,
      secondaryClick,
      fullWidthButtons: !isShowOnDesktop,
    }
  }, [
    isEditMode,
    isEditVariant,
    primaryButtonLoadingId,
    handleRedirect,
    isShowOnDesktop,
    handleCancel,
  ])

  const actionButtonProps = getActionButtonProps()

  return (
    <Box>
      <SpaceBetweenSection
        left={
          <DynamicBreadcrumbs
            breadcrumbs={[
              {
                label: t(
                  'Notifications.NotificationFormCard.Breadcrumb.Notifications',
                  'Notifications'
                ),
                onClick: handleRedirect,
              },
              {
                label: isEditVariant
                  ? t(
                      'Notifications.NotificationFormCard.Breadcrumb.EditNotification',
                      'Edit Notification'
                    )
                  : t(
                      'Notifications.NotificationFormCard.Breadcrumb.AddNotification',
                      'Add Notification'
                    ),
              },
            ]}
          />
        }
        right={
          <Typography variant="subtitle1" fontWeight={'bold'}>
            {t(
              'Notifications.Legend.RequiredFields',
              '* Indicates required field'
            )}
          </Typography>
        }
      />
      <ActionButtons {...actionButtonProps} />
      <NotificationInformationCard isEditMode={isEditMode} />
      <Box pt={2}>
        <NotificationRegionsCard isEditMode={isEditMode} />
      </Box>
      <Box pt={2}>
        <NotificationRolesCard isEditMode={isEditMode} />
      </Box>
      {/* Regions and Roles to go below */}
    </Box>
  )
}
